import { CENTER_REFRESH_MS, validCenterSnapshot } from "../../utils/centerAvailability.mjs";
import { centerPreviewEnabled } from "../../utils/centerAvailabilityPreview.mjs";

/** Ephemeral Vuex state: occupancy must never survive authentication changes. */
export function createCenterAvailabilityModule() {
    let pending = null;
    let generation = 0;
    return {
        namespaced: true,
        state: () => ({ enabled: false, preferenceError: false, snapshot: null, receivedAt: 0, attemptedAt: 0, waiting: {}, now: Date.now(), loading: false, error: false, dirty: false }),
        mutations: {
            reset(state) { generation++; pending = null; Object.assign(state, { enabled: false, preferenceError: false, snapshot: null, receivedAt: 0, attemptedAt: 0, waiting: {}, loading: false, error: false, dirty: false, now: Date.now() }); },
            waiting(state, { id, blocked }) {
                const waiting = { ...state.waiting };
                if (blocked) waiting[id] = true;
                else delete waiting[id];
                state.waiting = waiting;
            },
            attempted(state) { state.attemptedAt = Date.now(); },
            enabled(state, value) { state.enabled = value === true; },
            preferenceError(state) { state.preferenceError = true; },
            tick(state) { state.now = Date.now(); },
            invalidate(state) { generation++; state.dirty = true; },
            loading(state, value) { state.loading = value; },
            failure(state) { state.error = true; },
            loaded(state, data) { state.snapshot = Object.freeze(data); state.receivedAt = Date.now(); state.now = Date.now(); state.error = false; state.dirty = false; },
        },
        actions: {
            restorePreference({ commit }) {
                commit("reset");
            },
            refresh({ state, rootState, commit, dispatch }, { work, force = false, maxAge = CENTER_REFRESH_MS, background = false, canRefresh = () => true } = {}) {
                if (!centerPreviewEnabled(rootState) || !canRefresh()) return Promise.resolve(false);
                commit("tick");
                if (pending) return pending;
                // Throttle unsuccessful background attempts as well as successful reads.
                // Explicit invalidations and command completion still read immediately.
                if (background && state.attemptedAt && state.now - state.attemptedAt < maxAge) return Promise.resolve(false);
                if (!background && !force && !state.dirty && !state.error && state.receivedAt && state.now - state.receivedAt < maxAge) return Promise.resolve(true);
                const epoch = generation;
                commit("loading", true);
                commit("attempted");
                const request = work.get("/av/centri/disponibilita/", {}, true, null, { timeout: 10000 })
                    .then(response => {
                        if (epoch !== generation) return false;
                        if (!validCenterSnapshot(response?.data)) throw new Error("Invalid center availability");
                        commit("loaded", response.data); return true;
                    }).catch(() => { if (epoch === generation) commit("failure"); return false; })
                    .finally(() => {
                        if (pending !== request) return;
                        pending = null;
                        commit("loading", false);
                        // A mutation during the read requires exactly one trailing read.
                        if (epoch !== generation && state.dirty && canRefresh()) dispatch("refresh", { work, force: true, canRefresh });
                    });
                pending = request;
                return request;
            },
        },
    };
}
